import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IndustryAdmin } from "@app/shared";
import { FALLBACK_INDUSTRY_CODE } from "@app/shared";
import { industriesApi } from "../industries/api";
import { Button, Card, Field, Input, PageTitle, Select } from "../../components/ui";
import { QueryView } from "../../components/states";
import { ApiError } from "../../lib/api";

// 業種マスタ保守（§3.1/§4.1）。追加・改称・並べ替え・廃止/復活・統合。
// 「叩き台のままリリースし運用で直す」ための手段（§2.4）。全操作は監査に残る（サーバ側）。

interface AddForm {
  code: string;
  label: string;
  sortOrder: number;
}

export function AdminIndustriesPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["admin", "industries"], queryFn: industriesApi.listAdmin });
  // 保守用一覧と、企業フォーム/検索の選択肢キャッシュ（["industries","options"]）の両方を更新する。
  // 後者を落とさないと、改称・廃止が企業フォームの業種プルダウンに反映されない（codex#11）。
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "industries"] });
    qc.invalidateQueries({ queryKey: ["industries"] });
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="業種マスタ"
        subtitle="選択肢の追加・改称・並べ替え・廃止・統合ができます。廃止しても既存企業の表示は壊れません。"
      />
      <AddIndustryForm onDone={invalidate} />
      <QueryView query={query} emptyWhen={(d) => d.length === 0} emptyMessage="業種がありません。">
        {(rows) => <IndustryTable rows={rows} onChanged={invalidate} />}
      </QueryView>
    </div>
  );
}

function AddIndustryForm({ onDone }: { onDone: () => void }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddForm>({ defaultValues: { sortOrder: 100 } });

  const mutation = useMutation({
    mutationFn: (v: AddForm) =>
      industriesApi.create({ code: v.code, label: v.label, sortOrder: Number(v.sortOrder) }),
    onSuccess: () => {
      reset({ code: "", label: "", sortOrder: 100 });
      onDone();
    },
  });
  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <Card>
      <h3 className="mb-3 text-sm font-bold text-slate-600">業種を追加</h3>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={handleSubmit((v) => mutation.mutate(v))}
      >
        <div className="w-48">
          <Field label="コード（英小文字・作成後不変）" error={errors.code?.message}>
            <Input
              placeholder="例: welfare_service"
              {...register("code", {
                required: "コードは必須です",
                pattern: {
                  value: /^[a-z][a-z0-9_]{1,30}$/,
                  message: "英小文字で始まり英小文字・数字・_（2〜31文字）",
                },
              })}
            />
          </Field>
        </div>
        <div className="w-48">
          <Field label="表示名" error={errors.label?.message}>
            <Input
              placeholder="例: 福祉サービス"
              {...register("label", { required: "表示名は必須です" })}
            />
          </Field>
        </div>
        <div className="w-28">
          <Field label="並び順" error={errors.sortOrder?.message}>
            <Input type="number" {...register("sortOrder", { required: true })} />
          </Field>
        </div>
        <Button type="submit" disabled={mutation.isPending}>
          追加
        </Button>
      </form>
      {apiErr && <p className="mt-2 text-sm font-bold text-rose-500">{apiErr}</p>}
    </Card>
  );
}

function IndustryTable({ rows, onChanged }: { rows: IndustryAdmin[]; onChanged: () => void }) {
  const [err, setErr] = useState<string | null>(null);
  const onError = (e: unknown) =>
    setErr(e instanceof ApiError ? e.message : "操作に失敗しました。");

  const patch = useMutation({
    mutationFn: (v: { code: string; input: Parameters<typeof industriesApi.update>[1] }) =>
      industriesApi.update(v.code, v.input),
    onSuccess: () => {
      setErr(null);
      onChanged();
    },
    onError,
  });

  const reorder = useMutation({
    mutationFn: (order: { code: string; sortOrder: number }[]) => industriesApi.reorder(order),
    onSuccess: () => {
      setErr(null);
      onChanged();
    },
    onError,
  });

  const merge = useMutation({
    mutationFn: (v: { code: string; target: string }) => industriesApi.merge(v.code, v.target),
    onSuccess: () => {
      setErr(null);
      onChanged();
    },
    onError,
  });

  // 並べ替え: 位置を入れ替え、sort_order を振り直して一括保存（1トランザクション・§4.1）。
  const move = (index: number, dir: -1 | 1) => {
    const next = [...rows];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j]!, next[index]!];
    reorder.mutate(next.map((r, i) => ({ code: r.code, sortOrder: (i + 1) * 10 })));
  };

  const activeTargets = rows.filter((r) => r.isActive);
  const busy = patch.isPending || reorder.isPending || merge.isPending;

  return (
    <Card className="overflow-x-auto">
      {err && <p className="mb-2 text-sm font-bold text-rose-500">{err}</p>}
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs text-slate-400">
            <th className="py-2">並び</th>
            <th>コード</th>
            <th>表示名</th>
            <th>企業数</th>
            <th>状態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <IndustryRow
              key={r.code}
              row={r}
              first={i === 0}
              last={i === rows.length - 1}
              busy={busy}
              targets={activeTargets.filter((t) => t.code !== r.code)}
              onUp={() => move(i, -1)}
              onDown={() => move(i, 1)}
              onRename={(label) => patch.mutate({ code: r.code, input: { label } })}
              onToggle={() => patch.mutate({ code: r.code, input: { isActive: !r.isActive } })}
              onMerge={(target) => merge.mutate({ code: r.code, target })}
            />
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-slate-400">
        ※
        廃止（is_active=false）は新規の選択肢から外すだけで、その業種の企業は残ります。分類を実態に合わせるには「統合」で別業種へ付け替えてください。
      </p>
    </Card>
  );
}

function IndustryRow({
  row,
  first,
  last,
  busy,
  targets,
  onUp,
  onDown,
  onRename,
  onToggle,
  onMerge,
}: {
  row: IndustryAdmin;
  first: boolean;
  last: boolean;
  busy: boolean;
  targets: IndustryAdmin[];
  onUp: () => void;
  onDown: () => void;
  onRename: (label: string) => void;
  onToggle: () => void;
  onMerge: (target: string) => void;
}) {
  const [label, setLabel] = useState(row.label);
  const [mergeTarget, setMergeTarget] = useState("");

  return (
    <tr className="border-b border-slate-100 align-middle">
      <td className="py-2">
        <div className="flex gap-1">
          <button
            type="button"
            disabled={first || busy}
            onClick={onUp}
            className="disabled:opacity-30"
            aria-label="上へ"
          >
            ▲
          </button>
          <button
            type="button"
            disabled={last || busy}
            onClick={onDown}
            className="disabled:opacity-30"
            aria-label="下へ"
          >
            ▼
          </button>
        </div>
      </td>
      <td className="font-mono text-xs text-slate-500">{row.code}</td>
      <td>
        <div className="flex items-center gap-1">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="!w-40 !py-1.5"
          />
          {label.trim() !== row.label && (
            <Button
              type="button"
              variant="ghost"
              disabled={busy || label.trim() === ""}
              onClick={() => onRename(label.trim())}
            >
              改称
            </Button>
          )}
        </div>
      </td>
      <td className="tabular-nums">{row.companyCount}</td>
      <td>
        {row.isActive ? (
          <span className="rounded-pill bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
            有効
          </span>
        ) : (
          <span className="rounded-pill bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500">
            廃止
          </span>
        )}
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          {/* 受け皿（その他）は廃止できない（サーバも拒否・§2.4） */}
          {row.code !== FALLBACK_INDUSTRY_CODE && (
            <Button type="button" variant="ghost" disabled={busy} onClick={onToggle}>
              {row.isActive ? "廃止" : "復活"}
            </Button>
          )}
          {/* 統合は廃止済みの業種でも可能にする（残った企業を別業種へ回収する導線・codex#10）。
              受け皿（その他）は統合元にできない。統合先は active のみ（targets は active）。 */}
          {row.code !== FALLBACK_INDUSTRY_CODE && targets.length > 0 && (
            <>
              <Select
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
                className="!w-32 !py-1.5"
              >
                <option value="">統合先…</option>
                {targets.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </Select>
              <Button
                type="button"
                variant="danger"
                disabled={busy || !mergeTarget}
                onClick={() => {
                  const t = targets.find((x) => x.code === mergeTarget);
                  const tail = row.isActive ? `、「${row.label}」を廃止します` : "（廃止済み）";
                  if (
                    t &&
                    confirm(
                      `「${row.label}」の企業 ${row.companyCount} 社を「${t.label}」へ付け替えます${tail}。よろしいですか？`,
                    )
                  ) {
                    onMerge(mergeTarget);
                    setMergeTarget("");
                  }
                }}
              >
                統合
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
